import {
  LightDomStyles,
  OutlineElement,
  SlotsController,
  MobileController,
} from '@phase2/outline-core';

import { html, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import componentStyles from './outline-global-header.css.lit';
import outlineButtonBaselineCssLit from '../outline-button-baseline/outline-button-baseline.css.lit';
import { classMap } from 'lit/directives/class-map.js';

/**
 * The Global Header component
 */
@customElement('outline-global-header')
export class OutlineGlobalHeader extends OutlineElement {
  static styles = [componentStyles, outlineButtonBaselineCssLit];

  slots = new SlotsController(this);

  private mobileController = new MobileController(this, 'xl');

  lightDomStyles = new LightDomStyles(
    this,
    [componentStyles],
    this.tagName.toLowerCase()
  );

  /**
   * Indicates whether or not the search is open.
   * You can use this in lieu of the hide method.
   */
  @state() isSearchExpanded = false;

  /**
   * Indicates whether or not the dropdown is open.
   * You can use this in lieu of the hide method.
   */
  @state() isMobileHamburgerTrayOpen = false;

  /**
   * Indicated whether there is a admin toolbar so that
   * we can adjust positioning.
   */
  @state()
  isAdminToolbarOpen = false;

  updated(): void {
    if (this.mobileController.isMobile) {
      this.isSearchExpanded = true;
    }

    this.isAdminToolbarOpen =
      !!this.closest('body')?.classList.contains('toolbar-icon-9');

    // On mobile, when the tray is open, disable scrolling on the body
    if (this.isMobileHamburgerTrayOpen && this.mobileController.isMobile) {
      document
        .querySelector('body')
        ?.classList.add('overflow-hidden', 'h-screen');
    } else {
      document
        .querySelector('body')
        ?.classList.remove('overflow-hidden', 'h-screen');
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('keydown', this._handleKeyDown);
  }

  // Will trigger the native search form.
  triggerSearch(e: Event) {
    const validNodeNames = ['BUTTON', 'OUTLINE-ICON-BASELINE'];
    const targetNodeName = (e.target as Node).nodeName;

    if (validNodeNames.includes(targetNodeName)) {
      const formElement = this.querySelector(
        'form#header-search-form'
      ) as HTMLFormElement | null;

      if (formElement) {
        formElement.submit();
      }
    }
  }

  render(): TemplateResult {
    const isMobile = this.mobileController.isMobile;
    return html`
      <header class="${this.isMobileHamburgerTrayOpen ? 'is-open' : ''}">
        <div
          class="${classMap({
            'global-header': true,
            'toolbar': this.isAdminToolbarOpen,
          })}"
        >
          <outline-container-baseline
            background-color="blue"
            content-max-width="medium"
            y-padding="none"
            x-padding="true"
          >
            ${isMobile ? this._mobileTemplate() : this._desktopTemplate()}
          </outline-container-baseline>
        </div>
      </header>
    `;
  }

  _handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.isSearchExpanded = false;
      this.isMobileHamburgerTrayOpen = false;
    }
  }

  _mobileTemplate(): TemplateResult {
    return html`
      <div class="top-row">
        ${this.slots.conditionalSlot('logo', false)}

        <button
          class="hamburger-menu-trigger btn small primary"
          aria-label="${this.isMobileHamburgerTrayOpen
            ? 'close menu flyout'
            : 'Open menu flyout'}"
          aria-controls="mobile-hamburger-tray"
          @click="${() =>
            (this.isMobileHamburgerTrayOpen = !this.isMobileHamburgerTrayOpen)}"
        >
          <outline-icon
            name="menu"
            library="material"
            size="1.25rem"
          ></outline-icon>
        </button>
      </div>
      <div
        id="mobile-hamburger-tray"
        class="mobile-hamburger-tray ${this.isMobileHamburgerTrayOpen
          ? 'expanded'
          : null}"
      >
        ${this._mobileSearchTemplate()}
        ${this.slots.conditionalSlot('quick-links-navigation', false)}
        ${this.slots.conditionalSlot('main-navigation', false)}
        ${this.slots.conditionalSlot('utility-navigation', false)}
      </div>
    `;
  }

  _desktopTemplate(): TemplateResult {
    return html`
      <div class="top-row">
        ${this.slots.conditionalSlot('logo', false)}
        ${this.slots.conditionalSlot('main-navigation', false)}
        <div class="utility-wrapper">
          ${this.slots.conditionalSlot('utility-navigation', false)}
        </div>
        ${this.slots.conditionalSlot('quick-links-navigation', false)}
        ${this._desktopSearchTemplate()}
      </div>
    `;
  }

  _mobileSearchTemplate(): TemplateResult {
    return html`
      <div class="search-wrapper mobile">
        <div class="search-container expanded">
          <div class="search-form mobile">
            <slot name="search-form"></slot>
            <button class="btn small primary" @click=${this.triggerSearch}>
              <outline-icon-baseline
                name="eu-search"
                library="system"
                size="1rem"
                role="presentation"
              ></outline-icon-baseline>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  _desktopSearchTemplate(): TemplateResult {
    return html`
      <div
        class="${classMap({
          'search-wrapper': true,
          'desktop': true,
          'expanded': this.isSearchExpanded,
        })}"
      >
        <div
          class="${classMap({
            'search-container': true,
            'expanded': this.isSearchExpanded,
          })}"
        >
          <button
            class="btn link small white search-button"
            aria-label="${this.isSearchExpanded
              ? 'Close search'
              : 'Open search'}"
            aria-controls="search-form"
            aria-expanded="${this.isSearchExpanded ? 'true' : 'false'}"
            @click="${() => (this.isSearchExpanded = !this.isSearchExpanded)}"
          >
            ${this.isSearchExpanded
              ? html`<outline-icon-baseline
                  name="eu-x"
                  library="system"
                  size="1.5rem"
                ></outline-icon-baseline>`
              : html`<outline-icon-baseline
                  name="eu-search"
                  library="system"
                  size="1.5rem"
                ></outline-icon-baseline>`}
          </button>

          ${this.isSearchExpanded
            ? html` <div class="separator"></div> `
            : null}
          <div
            class="${classMap({
              'search-form': true,
              'expanded': this.isSearchExpanded,
            })}"
            id="search-form"
          >
            <slot name="search-form"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-global-header': OutlineGlobalHeader;
  }
}
